import { useAuth } from '@saltus/auth';
import { lazy, ReactNode, Suspense } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import Loading from '../components/loading/component';
import isInGroup from '../services/util/isInGroup';
import Landing from './landing';
import FormProvider from '../providers/FormProvider';

// Lazy load views in order to speed up initial load times and reduce bundle sizes
const A = lazy(() => import('./a'));
const AppAuth = lazy(() => import('./app-auth'));
const Admin = lazy(() => import('./admin'));
const AdminResetMfa = lazy(() => import('./admin-reset-mfa'));
const Adviser = lazy(() => import('./adviser'));
const AdminChangeEmail = lazy(() => import('./admin-change-email'));
const AdminAddNewUser = lazy(() => import('./admin-add-new-user'));
const AdminChangePassword = lazy(() => import('./admin-change-password'));
const AdminResendInvite = lazy(() => import('./admin-resend-invite'));
const AdminViewClientWithdrawals = lazy(
  () => import('./admin-view-client-withdrawals'),
);
const AdminViewClientDocumentAnalytics = lazy(
  () => import('./admin-view-client-document-analytics'),
);
const AdminViewAdviserDocumentAnalytics = lazy(
  () => import('./admin-view-adviser-document-analytics'),
);
const Atr = lazy(() => import('./atr'));
const Withdrawals = lazy(() => import('./withdrawals'));
const PlanOwner = lazy(() => import('./plan-owner'));
const Dashboard = lazy(() => import('./dashboard'));
const AddMoney = lazy(() => import('./add-money'));
const Documents = lazy(() => import('./documents'));
const Security = lazy(() => import('./security'));
const DocumentViewer = lazy(() => import('./document-viewer'));
const Plans = lazy(() => import('./plans'));
const WithdrawMoneyContactDetails = lazy(
  () => import('./withdraw-money-contact-details'),
);
const ContactUs = lazy(() => import('./contact-us'));
const CommissionAndCharges = lazy(() => import('./commission-and-charges'));
const PersonalDetails = lazy(() => import('./personal-details'));
const DashboardMaintenance = lazy(() => import('./dashboard-maintenance'));
// eslint-disable-next-line @typescript-eslint/no-unsafe-return, import/extensions -- TS container uses connect() which returns any
const AccountMaintenance = lazy(() => import('./account-maintenance'));
const TestMode = lazy(() => import('./test-mode'));
// eslint-disable-next-line @typescript-eslint/no-unsafe-return, import/extensions -- TS container uses connect() which returns any
const ActionCentre = lazy(() => import('./action-centre'));
const ActionSummary = lazy(() => import('./action-centre-action-summary'));
const MfaEnrolment = lazy(() => import('./mfa-enrolment'));
const ForwardLink = lazy(() => import('./forward-link'));
const Form = lazy(() => import('./form'));
const NotFound = lazy(() => import('./_404'));

interface ProtectedRouteProps {
  children?: ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();

  const location = useLocation();

  if (loading) {
    return <Loading />;
  }

  if (user) {
    return children ? <>{children}</> : <Outlet />;
  }

  return <Navigate to={`/?returnTo=${location.pathname}`} replace />;
}

/**
 * Special element used to decide what to display on the index route.
 */
function IndexRoute() {
  const { user, loading } = useAuth();

  const [searchParams] = useSearchParams();

  if (loading) {
    return <Loading />;
  }

  if (user) {
    const returnTo = searchParams.get('returnTo');

    if (returnTo) {
      return <Navigate to={returnTo} replace />;
    }

    if (isInGroup(user, 'Admin')) {
      return <Navigate to="/admin" />;
    }

    if (isInGroup(user, 'Adviser')) {
      return <Navigate to="/adviser" />;
    }

    return <Navigate to="/dashboard" />;
  }

  return <AppAuth />;
}

function RouterOutlet() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route index element={<IndexRoute />} />

        <Route path="/activateaccount" element={<ForwardLink />} />

        <Route path="/resetpassword" element={<ForwardLink />} />

        <Route
          path="atr/*"
          element={
            <ProtectedRoute>
              <Atr />
            </ProtectedRoute>
          }
        />

        <Route
          path="dashboard/:planownerSlug/plans/:planId/withdraw-money/*"
          element={
            <ProtectedRoute>
              <Withdrawals />
            </ProtectedRoute>
          }
        />

        <Route path="a/:shortId" element={<A />} />

        <Route
          path="account-maintenance"
          element={
            <ProtectedRoute>
              <AccountMaintenance />
            </ProtectedRoute>
          }
        />

        <Route path="actions/*" element={<ProtectedRoute />}>
          <Route index element={<ActionCentre />} />
          <Route path="actionsummary/:guid" element={<ActionSummary />} />
        </Route>

        <Route path="admin/*" element={<ProtectedRoute />}>
          <Route index element={<Admin />} />
          <Route path="resend-invite" element={<AdminResendInvite />} />
          <Route
            path="view-client-withdrawals"
            element={<AdminViewClientWithdrawals />}
          />
          <Route
            path="view-client-document-analytics"
            element={<AdminViewClientDocumentAnalytics />}
          />
          <Route
            path="view-adviser-document-analytics"
            element={<AdminViewAdviserDocumentAnalytics />}
          />
          <Route path="add-new-user" element={<AdminAddNewUser />} />
          <Route path="change-email" element={<AdminChangeEmail />} />
          <Route path="change-password" element={<AdminChangePassword />} />
          <Route path="reset-2fa" element={<AdminResetMfa />} />
        </Route>

        <Route path="adviser/*" element={<ProtectedRoute />}>
          <Route index element={<Adviser />} />
          <Route
            path="view-client-withdrawals"
            element={<AdminViewClientWithdrawals />}
          />
          <Route
            path="view-client-document-analytics"
            element={<AdminViewClientDocumentAnalytics />}
          />
        </Route>

        <Route
          path="commission-and-charges"
          element={
            <ProtectedRoute>
              <CommissionAndCharges />
            </ProtectedRoute>
          }
        />

        <Route
          path="contact-us"
          element={
            <ProtectedRoute>
              <ContactUs />
            </ProtectedRoute>
          }
        />

        <Route path="dashboard/*" element={<ProtectedRoute />}>
          <Route index element={<Dashboard />} />
          <Route path=":planownerSlug" element={<PlanOwner />} />
          <Route path=":planownerSlug/plans/:planId" element={<Plans />} />
          <Route
            path=":planownerSlug/plans/:planId/add-money"
            element={<AddMoney />}
          />
          <Route
            path=":planownerSlug/plans/:planId/withdraw-money-contact-details"
            element={<WithdrawMoneyContactDetails />}
          />
        </Route>

        <Route path="documents/*" element={<ProtectedRoute />}>
          <Route index element={<Documents />} />
          <Route path="document-viewer" element={<DocumentViewer />} />
        </Route>

        <Route
          path="landing/:id/:resourceType/:resourceId"
          element={<Landing />}
        />

        <Route
          path="maintenance"
          element={
            <ProtectedRoute>
              <DashboardMaintenance />
            </ProtectedRoute>
          }
        />

        <Route
          path="personal-details"
          element={
            <ProtectedRoute>
              <PersonalDetails />
            </ProtectedRoute>
          }
        />

        <Route
          path="security"
          element={
            <ProtectedRoute>
              <Security />
            </ProtectedRoute>
          }
        />

        <Route path="testmode" element={<TestMode />} />

        <Route
          path="2fa-enrolment/*"
          element={
            <ProtectedRoute>
              <MfaEnrolment />
            </ProtectedRoute>
          }
        />

        <Route
          path="form/:id"
          element={
            <ProtectedRoute>
              <FormProvider>
                <Form />
              </FormProvider>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default RouterOutlet;
